import { useRef, useState } from 'react';
import { Menu } from 'primereact/menu';
import { Toast } from 'primereact/toast';
import { Avatar } from 'primereact/avatar';

import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/store/hooks';
import { processTitleCase } from '@/helpers/utils';
import { useNavigate } from 'react-router';
import ChangePsPopup from './change-password';
import { UpdateProfilePopup } from './update-profile/UpdateProfilePopup';
import { useCheckUserIsCustomer } from '@/hooks/useCheckUserIsCustomer';
import { cn } from '@/helpers/utils/functionality-utils';
import { Button } from 'primereact/button';
import { AnimatePresence, motion } from 'motion/react';
import { slideVariants } from '@/helpers/ComponentUtils';

const ProfileMenu = ({ isMobileMenu }: { isMobileMenu?: boolean }) => {
  const [isOpenForgetPsPopup, setIsOpenForgetPsPopup] = useState(false);
  const [isOpenProfile, setIsOpenProfile] = useState(false);
  const nav = useNavigate();
  const toast = useRef<Toast>(null);
  const menuLeft = useRef<Menu>(null);
  const { t } = useTranslation('common');

  const { isUserCustomer } = useCheckUserIsCustomer();

  const [isOpenUpdateProfile, setIsOpenUpdateProfile] = useState(false);

  const authUserData = useAppSelector((state) => state.user.authUserData.data);
  const name = authUserData?.name;
  const tenantName = authUserData?.tenant?.name;
  const profilePic = authUserData?.pic;

  const userPermissions = useAppSelector(
    (state) => state.user.authUserData.userPermissions,
  );

  const items = [
    {
      template: () => (
        <button
          type="button"
          onClick={() => {
            if (userPermissions['profile_update'] || isUserCustomer)
              setIsOpenUpdateProfile(true);
          }}
          className="p-link flex w-full items-center rounded-none p-2 pl-6 hover:bg-surface-200"
        >
          <div className="flex items-center gap-2 md:gap-4">
            <div className="relative !size-14 shrink-0 overflow-hidden rounded-full border">
              {profilePic ? (
                <img
                  src={encodeURI(profilePic)}
                  alt="profile pic"
                  className="size-full object-cover"
                />
              ) : (
                <span className="grid size-full place-items-center">
                  <i className="pi pi-user text-2xl md:text-3xl" />
                </span>
              )}
            </div>
            <div className="flex h-auto flex-col justify-center gap-2">
              <div className="">
                <div className="text-lg">{name}</div>
              </div>
              <div className="text-sm font-semibold">
                {processTitleCase(tenantName)}
              </div>
            </div>
          </div>
        </button>
      ),
    },
    { separator: true },
    {
      label: t('My business'),
      items: [
        {
          label: t('Policy terms'),
          icon: 'pi pi-file',
          to: '',
        },
      ],
    },
    {
      label: t('Profile'),
      items: [
        {
          label: t('Change Password'),
          icon: 'pi pi-refresh',
          command: () => {
            setIsOpenForgetPsPopup(true);
          },
        },
      ],
    },
  ];

  if (userPermissions['settings_view']) {
    items[3].items?.unshift({
      label: t('Settings'),
      icon: 'pi pi-cog',
      to: '',
      command: () => {
        if (userPermissions['settings_view']) {
          nav('/main/settings', {
            viewTransition: true,
          });
        }
      },
    });
  }

  return (
    <div className="relative sm:m-0 min-[1200px]:left-0">
      <UpdateProfilePopup
        open={isOpenUpdateProfile}
        setOpen={setIsOpenUpdateProfile}
      />
      <Toast ref={toast} />
      <ChangePsPopup
        open={isOpenForgetPsPopup}
        setOpen={setIsOpenForgetPsPopup}
      />
      {isMobileMenu ? (
        <Button
          type="button"
          onClick={() => setIsOpenProfile(!isOpenProfile)}
          severity="secondary"
          text
          className="flex w-full gap-3 shadow-none"
        >
          <div className="relative size-5 shrink-0">
            <div
              className={cn(
                'absolute right-0 top-1/2 size-7 shrink-0 -translate-y-1/2 rounded-full border-[3px] border-green-300',
                isUserCustomer && 'border-transparent',
              )}
            >
              {!isUserCustomer && (
                <div className="absolute -bottom-1 -right-1 size-4 rounded-full border border-white bg-green-400" />
              )}
              {profilePic ? (
                <Avatar
                  image={authUserData.pic}
                  shape="circle"
                  template={
                    <img
                      src={encodeURI(authUserData.pic)}
                      alt="profile pic"
                      className="absolute right-1/2 top-1/2 size-5 -translate-y-1/2 translate-x-1/2 object-cover"
                    />
                  }
                />
              ) : (
                <i className="pi pi-user relative !m-0 !text-2xl" />
              )}
            </div>
          </div>
          {t('Profile')}
        </Button>
      ) : (
        <button
          title={t('Profile')}
          type="button"
          className="p-link layout-topbar-button"
          onClick={(event) => menuLeft.current?.toggle(event)}
          aria-controls="popup_menu_left"
          aria-haspopup
        >
          <div
            className={cn(
              'relative grid size-11 shrink-0 place-items-center rounded-full border-[3px] border-green-300 p-0.5',
              isUserCustomer && 'border-transparent',
            )}
          >
            {!isUserCustomer && (
              <div className="absolute -bottom-1 -right-1 size-4 rounded-full border-[3px] border-white bg-green-400" />
            )}
            {profilePic ? (
              <Avatar
                image={authUserData.pic}
                shape="circle"
                template={
                  <img
                    src={encodeURI(authUserData.pic)}
                    alt="profile pic"
                    className="size-full object-cover"
                  />
                }
              />
            ) : (
              <i className="pi pi-user relative !m-0 !text-2xl" />
            )}
          </div>
          <span className="ml-4">{t('Profile')}</span>
        </button>
      )}
      {isMobileMenu ? (
        <AnimatePresence>
          {isOpenProfile && (
            <motion.div
              variants={slideVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Menu
                model={items}
                ref={menuLeft}
                id="popup_menu_left"
                className="w-full max-w-80 md:w-80"
              />
            </motion.div>
          )}
        </AnimatePresence>
      ) : (
        <Menu
          model={items}
          popup
          ref={menuLeft}
          id="popup_menu_left"
          className="w-full max-w-80 md:w-80"
        />
      )}
    </div>
  );
};

export default ProfileMenu;
